/**
 * The handful of applications we recognise, and why recognition is keyed on the HOST.
 *
 * <h3>The name is a claim; the host is evidence</h3>
 * Dynamic client registration is unauthenticated by design — that is what lets an
 * assistant register itself without anybody at either company doing paperwork — so
 * anyone who can reach `/register` can call themselves "Claude". A badge keyed on the
 * NAME would therefore badge the impersonator, which is worse than no badge: it would
 * put our own assurance behind the thing it was supposed to warn about.
 *
 * So recognition is keyed on the **redirect host**, which is where the authorisation
 * code physically goes. An impersonator can claim the name and cannot claim the host,
 * because claiming it would mean sending the code to the real application instead of to
 * themselves — which defeats the point of impersonating.
 *
 * <h3>Why this is only safe as of the signature work</h3>
 * The host reaches this screen as a query parameter. Until the MCP server started
 * signing it (and core-service verifying), **anyone could link somebody here with
 * `client_host=claude.ai`** — so a host-keyed badge would have been forgeable too, just
 * one step further along. The badge is exactly as trustworthy as the host, and the host
 * is now signed end to end. That ordering was not optional: this file could not have
 * been written honestly before that landed.
 *
 * <p>It is still worth knowing what this badge does NOT prove. It says "the code goes to
 * a host we recognise". It does not say the application behind that host is behaving, and
 * it is not an endorsement of what the person is about to grant — the permission list
 * below it is still the thing to read.
 *
 * <h3>Adding an entry</h3>
 * Add the host an application's authorisation code is actually delivered to — not its
 * marketing domain, and not a host it merely also owns. If in doubt, leave it out: an
 * unrecognised real client renders as unverified, which is a mild cosmetic loss, while a
 * wrongly recognised one is a lie on a consent screen.
 */
export const VERIFIED_CLIENTS: Record<string, string> = {
  // Anthropic — Claude's connector callback.
  'claude.ai': 'Claude',
  'claude.com': 'Claude',
  // OpenAI — ChatGPT connectors/apps.
  'chatgpt.com': 'ChatGPT',
  'chat.openai.com': 'ChatGPT',
  'openai.com': 'ChatGPT',
};

export interface ClientIdentity {
  /** True only when the host is one we recognise. */
  verified: boolean;
  /** The name WE assert for a recognised host, which beats whatever the client claimed. */
  verifiedName?: string;
  /**
   * True when the client CLAIMS a name we recognise but arrives from a host we do not.
   *
   * <p>The impersonation case, and the reason the badge is keyed on the host. Worth
   * surfacing distinctly rather than just leaving it unverified: "this calls itself
   * Claude and is not Claude" is a far stronger warning than "unverified", and it is
   * the only case on this screen where we know something is actively wrong.
   */
  impersonating: boolean;
}

/** Hosts are compared case-insensitively and without a port, as DNS is. */
function normaliseHost(host: string): string {
  return host.trim().toLowerCase().replace(/:\d+$/, '');
}

/**
 * What we are willing to say about this application.
 *
 * @param clientHost the redirect host — signed by the MCP server, so this is evidence
 * @param claimedName whatever the client called itself at registration — a claim only
 */
export function identify(clientHost: string, claimedName: string): ClientIdentity {
  const host = normaliseHost(clientHost || '');
  const verifiedName = host ? VERIFIED_CLIENTS[host] : undefined;
  if (verifiedName) {
    return { verified: true, verifiedName, impersonating: false };
  }

  // Not a host we know. Does it nonetheless claim a name we do?
  const claim = (claimedName || '').trim().toLowerCase();
  const impersonating = claim.length > 0
    && Object.values(VERIFIED_CLIENTS).some((name) => {
      const n = name.toLowerCase();
      // Whole-word match, so "Claude" is caught but a legitimately different product
      // whose name merely contains it is not flagged on a substring alone.
      return claim === n || new RegExp(`(^|[^a-z0-9])${n}([^a-z0-9]|$)`).test(claim);
    });

  return { verified: false, impersonating };
}
